/* eslint-disable react/no-unknown-property */

import React, { useEffect, useRef, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';

import { IServerOrder } from '@rbi-ctg/menu';
import { getErrorMessage } from 'components/credit-card-form-inputs';
import { SaveCardForFutureLabel } from 'components/credit-card-form-inputs/credit-card-form-labels';
import FormTitle from 'components/credit-card-form-inputs/credit-card-form-title';
import LoadingAnimation from 'components/loading-animation';
import {
  ICreatePaycometPreAuthorizationMutation,
  IGeneratePaycometCheckoutLinkMutation,
  useCreatePaycometPreAuthorizationMutation,
  useGeneratePaycometCheckoutLinkMutation,
} from 'generated/rbi-graphql';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
// TODO: Refactor to use useLogger hook => ICFE-189
import { StatusType, dataDogLogger } from 'utils/datadog';
import { expiryMask, zipCodeMask } from 'utils/masks';
import { IPaycometState, IPaymentErrors } from 'utils/payment';

import { Card, OutcomeResult } from '../types';

import Modal3DS from './modal-3ds';
import { PaycometCreditCardPaymentContainer } from './paycomet-credit-card-form.styled';
import {
  CardNumberPaycometContainer,
  CardNumberPaycometStyle,
  CvvField,
  CvvPaycometStyle,
  ErrorItem,
  Expiry,
  Form,
  FormContainer,
  InfoPaycometContainer,
  PaycometItem,
  StyledCheckbox,
  StyledSaveCardContainer,
  TextInput,
  TextInputPaycomet,
  ZipCode,
} from './styled';

export interface IPaycometCreditCardPaymentFormProps {
  onResult: (outcome: OutcomeResult) => void;
  onChange: (name: string, value: string) => void;
  paymentValues: IPaycometState;
  errors: IPaymentErrors;
  rbiOrder: IServerOrder;
  tpvToken: string;
  onError: (onError: boolean) => void;
}

export const PaycometCreditCardHostedPaymentForm: React.FC<IPaycometCreditCardPaymentFormProps> = ({
  onChange,
  paymentValues,
  errors,
  rbiOrder,
  onResult,
  tpvToken,
  onError,
}) => {
  const enableSaveCardPaycomet = useFlag(LaunchDarklyFlag.ENABLE_SAVE_CARD_PAYCOMET);
  const [paymentJetId, setPaymentJetId] = useState('');
  const [dateMonth, setDateMonth] = useState('');
  const [dateYear, setDateYear] = useState('');
  const [url3ds, setUrl3ds] = useState('');
  const paymentMethod = useRef({});

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const { feCountryCode } = useLocale();
  const { formatMessage } = useIntl();
  const getErrorMessages = getErrorMessage({ errors, formatMessage });

  const [errorMessage, setErrorMessage] = useState(getErrorMessages || '');

  const stateSaveCard = enableSaveCardPaycomet ? paymentValues.saveCard : false;
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  // If the country field is hidden from the form, use the user's current locale
  const country = paymentFieldVariations.country ? paymentValues.billingCountry : feCountryCode;

  // retrieve additional credit card form props
  const { postalCodeInputProps } = useCreditCardFormProps(country ?? undefined);

  const PAYCOMET_LOG_PREFIX = '[paycomet-credit-card-form]';

  const handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement> = e => {
    onChange(e.target.name, e.target.value);
  };

  const handlePaycometLinkCompleted = (result: IGeneratePaycometCheckoutLinkMutation) => {
    if (!result?.generatePaycometCheckoutLink?.jetId) {
      throw new Error(`Is missing jetId`);
    }

    dataDogLogger({
      message: `${PAYCOMET_LOG_PREFIX} handlePaycometLinkCompleted`,
      status: StatusType.info,
    });
    setPaymentJetId(result.generatePaycometCheckoutLink.jetId);
  };

  const [generatePaycometLink] = useGeneratePaycometCheckoutLinkMutation({
    onCompleted: handlePaycometLinkCompleted,
    onError: (error: any) => {
      setErrorMessage(formatMessage({ id: 'paymentDetailsError' }));
      setIframeLoaded(false);
      onError(true);

      dataDogLogger({
        message: 'Paycomet: Non-vaulted CC integration threw an error',
        status: StatusType.error,
      });

      throw error;
    },
  });

  const handleCreatePreAuthorization = (result: ICreatePaycometPreAuthorizationMutation) => {
    if (result && result.createPaycometPreAuthorization.paymentMethod) {
      paymentMethod.current = result.createPaycometPreAuthorization.paymentMethod;
      if (result.createPaycometPreAuthorization.paymentMethod.url3ds) {
        dataDogLogger({
          message: `${PAYCOMET_LOG_PREFIX} 3DS required`,
          status: StatusType.info,
        });
        setOpenModal(true);
        setUrl3ds(result.createPaycometPreAuthorization.paymentMethod.url3ds);
      }
      setIframeLoaded(false);
      dataDogLogger({
        message: `${PAYCOMET_LOG_PREFIX} finishing generateCreatePreAuthorization`,
        status: StatusType.info,
      });
    }
  };

  const handleErrorPreAuthorization = (error: any) => {
    setErrorMessage(formatMessage({ id: 'paymentDetailsError' }));
    setIframeLoaded(false);
    dataDogLogger({
      message: 'Paycomet: Non-vaulted CC integration threw an error',
      status: StatusType.error,
    });

    throw error;
  };

  const [generateCreatePreAuthorization] = useCreatePaycometPreAuthorizationMutation({
    onCompleted: handleCreatePreAuthorization,
    onError: handleErrorPreAuthorization,
  });

  useEffect(() => {
    if (rbiOrder?.rbiOrderId && !tpvToken) {
      dataDogLogger({
        message: `${PAYCOMET_LOG_PREFIX} starting generatePaycometLink`,
        status: StatusType.info,
      });
      generatePaycometLink({
        variables: { rbiOrderId: rbiOrder.rbiOrderId, typePayment: 'CREDIT' },
      });
    }
    if (rbiOrder?.rbiOrderId && tpvToken) {
      dataDogLogger({
        message: `${PAYCOMET_LOG_PREFIX} starting generateCreatePreAuthorization`,
        status: StatusType.info,
      });
      setIframeLoaded(true);
      generateCreatePreAuthorization({
        variables: {
          input: {
            rbiOrderId: rbiOrder.rbiOrderId,
            cardHolderName: paymentValues.nameOnCard,
            dateMonth,
            dateYear,
            saveCard: stateSaveCard,
            ipOrigin: '',
            paytpvToken: tpvToken,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatePaycometLink, rbiOrder.rbiOrderId, tpvToken]);

  useEffect(() => {
    if (!paymentJetId) {
      return undefined;
    }

    function listener(event: MessageEvent) {
      const origin = new URL(event.origin);
      if (!origin.hostname.endsWith('.rbictg.com')) {
        return;
      }

      if (event.data.type !== 'payment-outcome') {
        return;
      }

      const body = event.data.body as OutcomeResult;

      if (body.type === 'Success') {
        body.billingZip = paymentValues.billingZip;
        body.pspReference = rbiOrder.rbiOrderId;

        body.paymentMethod = {
          ...paymentMethod.current,
          jetId: paymentJetId,
        } as Card;
      } else {
        return abort3DSChallenge();
      }

      dataDogLogger({
        message: `${PAYCOMET_LOG_PREFIX} payment-outcome. Closing modal`,
        status: StatusType.info,
      });

      setOpenModal(false);
      setIframeLoaded(false);
      onResult(body);
    }

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onResult, paymentJetId]);

  const abort3DSChallenge = () => {
    dataDogLogger({
      message: `${PAYCOMET_LOG_PREFIX} 3DS Challenge aborted`,
      status: StatusType.info,
    });
    window.location.href = '/cart';
    setOpenModal(false);
  };

  useEffect(() => {
    const month = paymentValues.expiry?.slice(0, 2) || '';
    const year = paymentValues.expiry?.slice(3, 5) || '';

    setDateMonth(month);
    setDateYear(year);
  }, [paymentValues]);

  return (
    <>
      {iframeLoaded && <LoadingAnimation fillColor={Styles.color.primary} />}

      {openModal && (
        <Modal3DS
          onIframeLoaded={() => setIframeLoaded(false)}
          url={url3ds}
          loaded={iframeLoaded}
          onDismiss={() => abort3DSChallenge()}
        ></Modal3DS>
      )}

      <PaycometCreditCardPaymentContainer>
        {!paymentJetId && <LoadingAnimation fillColor={Styles.color.primary} />}
        {paymentJetId && (
          <>
            <Helmet>
              <script src="https://api.paycomet.com/gateway/paycomet.jetiframe.js?lang=es"></script>
            </Helmet>

            <FormContainer
              withPadding={true}
              withBorder={false}
              aria-label={'Paycomet'}
              isHidden={false}
            >
              {/* eslint-disable-next-line no-script-url */}
              <Form id="paycometPaymentForm" action="javascript:" noValidate>
                <FormTitle
                  title={formatMessage({ id: 'pleaseEnterYourCreditCardInformation' })}
                  creditText={formatMessage({ id: 'credit' })}
                />

                {paymentJetId && (
                  <TextInputPaycomet type="hidden" data-paycomet="jetID" value={paymentJetId} />
                )}

                <TextInput
                  aria-label={formatMessage({ id: 'nameOnCard' })}
                  placeholder={formatMessage({ id: 'nameOnCard' })}
                  onChange={handleChange}
                  value={paymentValues.nameOnCard}
                  required
                  data-testid="name-card-id"
                  data-paycomet="cardHolderName"
                  name="nameOnCard"
                  autoComplete="cc-name"
                />

                <CardNumberPaycometContainer>
                  <PaycometItem
                    data-testid="paycomet-pan"
                    id="paycomet-pan"
                    data-paycomet="paNumber"
                  />

                  {/**
                   * This native input is required by paycomet provider,
                  because they need of the attributes: paycomet-name, paycomet-placeholder, paycomet-style
                  was not possible add it via styled component.
                  @link https://docs.paycomet.com/en/tarjetas/jetiframe
                  **/}
                  <input
                    id="paycomet-card-number"
                    data-testid="paycomet-card-number"
                    aria-label={formatMessage({ id: 'cardNumber' })}
                    paycomet-name="pan"
                    paycomet-placeholder={formatMessage({ id: 'cardNumber' })}
                    paycomet-style={CardNumberPaycometStyle}
                  />
                </CardNumberPaycometContainer>

                <InfoPaycometContainer>
                  <Expiry>
                    <MaskedInput
                      mask={expiryMask}
                      aria-label={formatMessage({ id: 'cardExpiration' })}
                      placeholder={formatMessage({ id: 'cardExpiration' })}
                      guide={false}
                      id="card-expiration-id"
                      onChange={handleChange}
                      value={paymentValues.expiry || ''}
                      data-testid="expiry"
                      name="expiry"
                    />

                    <TextInputPaycomet
                      data-testid="date-month"
                      id="dateMonth"
                      name="dateMonth"
                      type="hidden"
                      data-paycomet="dateMonth"
                      value={dateMonth}
                    />

                    <TextInputPaycomet
                      data-testid="date-year"
                      id="dateYear"
                      name="dateYear"
                      type="hidden"
                      data-paycomet="dateYear"
                      value={dateYear}
                    />
                  </Expiry>

                  <CvvField>
                    <PaycometItem data-testid="paycomet-cvc2" id="paycomet-cvc2" />

                    {/**
                     * This native input is required by paycomet provider,
                    because they need of the attributes: paycomet-name, paycomet-placeholder, paycomet-style
                    was not possible add it via styled component.
                    @link https://docs.paycomet.com/en/tarjetas/jetiframe
                    **/}
                    <input
                      aria-label={formatMessage({ id: 'cvv' })}
                      id="cvc2"
                      data-testid="cvc2"
                      paycomet-name="cvc2"
                      required
                      type="number"
                      paycomet-style={CvvPaycometStyle}
                      paycomet-placeholder={formatMessage({ id: 'cvv' })}
                    />
                  </CvvField>
                </InfoPaycometContainer>

                <ZipCode>
                  <MaskedInput
                    mask={zipCodeMask}
                    aria-label={formatMessage({ id: 'zipCode' })}
                    placeholder={formatMessage({ id: 'zipCode' })}
                    guide={false}
                    id="billing-zip"
                    onChange={handleChange}
                    value={paymentValues.billingZip || ''}
                    data-testid="billing-zip"
                    name="billingZip"
                    required
                    autoComplete="billing postal-code"
                    {...postalCodeInputProps}
                  />
                </ZipCode>
              </Form>
              <ErrorItem id="paymentErrorMsg">{errorMessage}</ErrorItem>
            </FormContainer>

            {enableSaveCardPaycomet && (
              <StyledSaveCardContainer>
                <StyledCheckbox
                  aria-label={formatMessage({ id: 'saveCardDisclaimer' })}
                  label={<SaveCardForFutureLabel />}
                  name="saveCard"
                  data-testid="save-card"
                  checked={stateSaveCard}
                  onChange={handleChange}
                />
              </StyledSaveCardContainer>
            )}
          </>
        )}
      </PaycometCreditCardPaymentContainer>
    </>
  );
};
